import moment from 'moment-timezone';
import * as React from 'react';

import '../../styles/members/simpleDateRangePickAndRun.scss';
import { ALL_TIME_FILTER } from '../workoutAggregateFilter';
import {
  generateMonths, generateWeeks, generateYears, IDropdownItem,
} from './dateRangePresets';

type PeriodType = 'all' | 'weeks' | 'months' | 'years' | 'custom';

interface IProps {
  currentFilter: any;
  onRun: (data: any) => void;
  timezone: string;
}

interface IState {
  period: PeriodType;
  from: string;
  to: string;
}

function getItemsForPeriod(period: PeriodType, timezone: string): IDropdownItem[] {
  switch (period) {
    case 'weeks': return generateWeeks(timezone);
    case 'months': return generateMonths(timezone);
    case 'years': return generateYears(timezone);
    default: return [];
  }
}

export default class SimpleDateRangePickAndRun extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      period: 'all',
      from: '',
      to: '',
    };
  }

  public render() {
    const { period, from, to } = this.state;
    const isAllTime = period === 'all';
    const isCustom = period === 'custom';
    const items = getItemsForPeriod(period, this.props.timezone);

    const hasValidPresetRange = isAllTime
      || (from !== '' && to !== '' && Number(from) >= Number(to));
    const hasValidCustomRange = from !== '' && to !== '' && from <= to;
    const hasValidRange = isCustom ? hasValidCustomRange : hasValidPresetRange;
    const isRunDisabled = !hasValidRange;

    return (
      <div className='simple-date-range-pick-and-run'>
        <select
          value={period}
          onChange={this.handlePeriodChange}
          className='simple-date-range-pick-and-run__select simple-date-range-pick-and-run__select--period'
        >
          <option value='all'>All time</option>
          <option value='weeks'>Weeks</option>
          <option value='months'>Months</option>
          <option value='years'>Years</option>
          <option value='custom'>Custom</option>
        </select>

        {!isAllTime && !isCustom && (
          <>
            <select
              value={from}
              onChange={this.handleFromChange}
              className='simple-date-range-pick-and-run__select simple-date-range-pick-and-run__select--from'
            >
              <option value='' disabled>Select...</option>
              {items.map((item, idx) => (
                <option key={idx} value={String(idx)}>{item.label}</option>
              ))}
            </select>
            <span className='simple-date-range-pick-and-run__dash'>-</span>
            <select
              value={to}
              onChange={(e) => this.setState({ to: e.target.value })}
              className='simple-date-range-pick-and-run__select simple-date-range-pick-and-run__select--to'
            >
              <option value='' disabled>Select...</option>
              {items.filter((item, idx) => from === '' || idx <= Number(from)).map((item, idx) => (
                <option key={idx} value={String(idx)}>{item.label}</option>
              ))}
            </select>
          </>
        )}

        {isCustom && (
          <>
            <input
              type='date'
              value={from}
              onChange={this.handleCustomFromChange}
              className='simple-date-range-pick-and-run__select simple-date-range-pick-and-run__select--from'
            />
            <span className='simple-date-range-pick-and-run__dash'>-</span>
            <input
              type='date'
              value={to}
              min={from || undefined}
              onChange={(e) => this.setState({ to: e.target.value })}
              className='simple-date-range-pick-and-run__select simple-date-range-pick-and-run__select--to'
            />
          </>
        )}

        <button
          onClick={this.handleRun}
          disabled={isRunDisabled}
          className='jr-btn-no-border simple-date-range-pick-and-run__run'
        >
          <img
            className='btnImg refreshIconContainer'
            src={isRunDisabled ? 'resources/icons/play-icon-disabled.svg' : 'resources/icons/play-icon.svg'}
            alt='Run'
          />
        </button>
      </div>
    );
  }

  private handleFromChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newFrom = e.target.value;
    const { to } = this.state;
    const needsToReset = to === '' || Number(to) > Number(newFrom);
    this.setState({ from: newFrom, to: needsToReset ? newFrom : to });
  };

  private handlePeriodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newPeriod = e.target.value as PeriodType;
    const defaultValue = newPeriod === 'all' || newPeriod === 'custom' ? '' : '0';
    this.setState({ period: newPeriod, from: defaultValue, to: defaultValue });
  };

  private handleCustomFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const from = e.target.value;
    const to = this.state.to < from ? from : this.state.to;
    this.setState({ from, to });
  };

  private handleRun = () => {
    const { period, from, to } = this.state;

    if (period === 'all') {
      this.props.onRun({
        ...ALL_TIME_FILTER,
        customFilter: true,
      } as any);
      return;
    }

    if (period === 'custom') {
      if (!from || !to || from > to) { return; }

      this.props.onRun({
        startDate: moment.tz(from, 'YYYY-MM-DD', this.props.timezone).startOf('day').toDate(),
        endDate: moment.tz(to, 'YYYY-MM-DD', this.props.timezone).endOf('day').toDate(),
        customFilter: true,
      });
      return;
    }

    const items = getItemsForPeriod(period, this.props.timezone);
    const fromIndex = Number(from);
    const toIndex = Number(to);

    if (isNaN(fromIndex) || isNaN(toIndex)) { return; }

    // Items are ordered most-recent-first, so higher index = older.
    // "from" should be the older (higher index), "to" the newer (lower index).
    const startDate = items[Math.max(fromIndex, toIndex)].startDate;
    const endDate = items[Math.min(fromIndex, toIndex)].endDate;

    if (startDate.getTime() <= endDate.getTime()) {
      this.props.onRun({
        startDate, endDate, customFilter: true,
      });
    }
  };
}
